import React, { useEffect, useMemo, useState } from "react";
import * as S from "./SurveyList.style.js";
import { useStateValue } from "../../../../../contextAPI/StateProvider.js";
import useRecordNavigation from "../../../../../hooks/useRecordNavigation.js";

// utils
import { Actions } from "../../../../../utils/actionsIds.js";
import * as P from "../../../../../utils/pipes.js";
import { useTranslation } from "react-i18next";
import ZaiaInput from "../../../../zaiaComponents/zaiaInput/ZaiaInput.jsx";
import SurveyBox from "../surveyBox/SurveyBox.jsx";
import Modal from "../../../../../utils/modal.js";
import { useQuery } from "@apollo/client";
import { QUERY_GET_ALL_SURVEYS_BY_ID_PROCESS_RANGE_DATES } from "../../../../../graphql/queries/Surveys.js";
import useDoctorData from "../../../../../hooks/useDoctorData.js";
import { QUERY_GET_PROCESS_NAMES } from "../../../../../graphql/queries/Process.js";
import PeriodOfTimeSelect from "../periodOfTimeSelect/PeriodOfTimeSelect.jsx";
import ZaiaReaction from "../../../../zaiaComponents/zaiaReaction/ZaiaReaction.jsx";
import ModalSurveys from "../modalSurveys/ModalSurveys.jsx";
import ZaiaSelect from "../../../../zaiaComponents/zaiaSelect/ZaiaSelect.jsx";
import FlorenzButton from "../../../../florenzComponents/florenzButton/FlorenzButton.jsx";
import FlorenzSelect from "../../../../florenzComponents/florenzSelect/FlorenzSelect.jsx";
import ZaiaToggleCircular from "../../../../zaiaComponents/zaiaToggleCircular";

// Assets
const logoZaiaLoading = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaReactions/singularity-health-loader.svg`;
const logoZaiaAlert = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaReactions/reaction-error-data.svg`;
const zaiaEmpty = `${process.env.REACT_APP_STORAGE_ZAIA_RESOURCES}/zaiaReactions/no-results.svg`;

function SurveyList({
  setSurveySelected
}) {

  const { recordAction } = useRecordNavigation();
  const [{ selectedPatient }, dispatch] = useStateValue();
  const [processSelected, setProcessSelected] = useState(selectedPatient?.userToProcesses[0].process?.id);
  const { i18n: { language }, t } = useTranslation("global");
  const [sentListActive, setSentListActive] = useState(true);  
  const [inactiveListActive, setInactiveListActive] = useState(true);  
  const { doctorData } = useDoctorData();
  const [modalSendSurveyActive, setModalSendSurveyActive] = useState(false);
  const [inputText, setInputText] = useState('');
  const [surveyPreview, setSurveyPreview] = useState();
  const [initialDateRange, setInitialDateRange] = useState();
  const [finishDateRange, setFinishDateRange] = useState();
  const [surveysAnsweredFilter, setSurveysAnsweredFilter] = useState(false);

  const { data: surveysData, loading: surveysDataLoading, error: surveysDataError } = useQuery(QUERY_GET_ALL_SURVEYS_BY_ID_PROCESS_RANGE_DATES, {
    variables: {
      processId: processSelected,
      initialDate: initialDateRange,
      finalDate: finishDateRange
    }
  })
  
  const { data: processData } = useQuery(QUERY_GET_PROCESS_NAMES);
  
  useEffect(() => {
    setProcessSelected(processData?.process.filter((process) => process.processToCustomForms.length > 1)[0]?.id)
  }, [processData]);

  const getFilterText = (text) => {
    return inputText 
      ? String(P.removeAccent(text)).toLowerCase().includes(P.removeAccent(inputText).toLowerCase())
      : true
  }

  const processesFiltered = useMemo(() => {
     return processData?.process.filter((process) => process.processToCustomForms.length > 1).map((process) => {
      return {
        value: process?.id,
        text: P.dataTranslation(process?.processName, language)
      }
    })
  }, [processData]);

  if(!processData) return null;

  if(processesFiltered.length === 0) 
    return <S.SurveyList>
      <ZaiaReaction
        zaiaImg={zaiaEmpty}
        text={t("surveys.noProgramWithSurvey")}
        // subText={}
        sizeText="33.25px"
        sizeSubText="15px"
        textColor="var(--blue-color)"
        widthimg="210px"
      />
    </S.SurveyList>

  return <S.SurveyList>
    <Modal open={surveyPreview}>
      <ModalSurveys
        setModalActive={setSurveyPreview}
        surveyForPreview={surveyPreview}
      />
    </Modal>
    <Modal open={modalSendSurveyActive}>
      <ModalSurveys
        setModalActive={setModalSendSurveyActive}        
      />
    </Modal>

    <div className="toolbar">
      <div className="toolbar__left">
        <div className="select-container">
          <div className="select-container__label">
            {t("surveys.surveysOf")}
          </div>
          <FlorenzSelect
            xsSize={true} 
            forWhiteBackgroud={false}
            items={processesFiltered}
            value={processSelected}
            setValue={(e) => {
              recordAction(Actions.OUTCOMES.SELECT_PROGRAM, e);
              setProcessSelected(e)
            }}
            heightList={"200px"}
            required
          />
        </div> 
        <div className="select-separator"></div>
        <div className="select-container">
          <div className="select-container__label">
            {t("surveys.surveysOf")}
          </div>
          <PeriodOfTimeSelect 
            initialDateRange={initialDateRange}
            finishDateRange={finishDateRange}
            setInitialDateRange={setInitialDateRange}
            setFinishDateRange={setFinishDateRange}
          />
        </div>
      </div>
      <div className="toolbar__right">
        <FlorenzButton
          typeStyle={"primary"}
          size="s"
          text={t("globally.newSurvey")}
          widthComplete={true}
          onClick={() => {
            // recordAction(Actions.AINAVIGATOR_MODAL.CHANGE_PROGRAM_STATUS);
            setModalSendSurveyActive(true)
          }}
        />
      </div>
    </div>

    <div className="section">
      <div className="section__content">
        <div className="section__left">
          <div className="all-surveys-quantity">
            {t("surveys.allSurveysAvailable")} {surveysData?.customForm.filter((survey) => getFilterText(P.dataTranslation(survey.customFormName, language)))?.length}
          </div>
        </div>
        <div className="section__right">
          <div className="survey-answered-filter">
            <div className="survey-answered-filter__text">
              {t("surveys.seeSurveysWithResults")} <b>{surveysData?.customForm.filter((survey) => getFilterText(P.dataTranslation(survey.customFormName, language))).filter(survey => survey.userCustomFormHistories.length > 0).length}</b>
            </div>
            <ZaiaToggleCircular 
              active={surveysAnsweredFilter}
              toggle={setSurveysAnsweredFilter}
              color="#2A28D6"
            />
          </div>
        </div>
      </div>
    </div>
    <div className="surveys-section">
      {
        sentListActive &&
        <SurveysListContainer
          surveys={surveysData?.customForm.filter((survey) => getFilterText(P.dataTranslation(survey.customFormName, language)))}
          surveysError={surveysDataError}
          surveysLoading={surveysDataLoading}
          setSurveySelected={setSurveySelected}
          setSurveyPreview={setSurveyPreview}
          surveysAnsweredFilter={surveysAnsweredFilter}
        />
      }
    </div>
  </S.SurveyList>
}

const SurveysListContainer = ({ 
  surveys,
  surveysError,
  surveysLoading,
  setSurveySelected,
  setSurveyPreview,
  surveysAnsweredFilter
 }) => {
  
  const { i18n: { language }, t } = useTranslation("global");
  
  if(surveysLoading) 
    return (
      <div className="survey-list__reaction">
        <ZaiaReaction
          widthimg="150px"
          zaiaImg={logoZaiaLoading}
        /> 
      </div>
    )

  if(surveysError) 
    return (
      <div className="survey-list__reaction">
        <ZaiaReaction
          zaiaImg={logoZaiaAlert}
          widthimg="100px"
          text={t("globally.errorLoadingSurveys")}
          sizeText="15px"
          widthText="240px"
        />
      </div>
    )

  if(surveys.length === 0) 
    return (
      <div className="survey-list__reaction">
        <ZaiaReaction
          zaiaImg={zaiaEmpty}
          text={t("surveys.noSurveys")}
          // subText={}
          sizeText="33.25px"
          sizeSubText="15px"
          textColor="var(--blue-color)"
          widthimg="210px"
        />
      </div>
    )    
    
  return (
    <div className="survey-list">
      {
        surveys
        .filter((survey) => surveysAnsweredFilter ? survey.userCustomFormHistories.length : true)
        .map((survey) => {
          return (
            <div className="survey-item" key={survey.id}>
              <div className="survey-item__info">
                {
                  survey.isInteractive &&
                  <div className="survey-item__info-icon-container">
                    <i className={`survey-item__info-icon icon zaia-i-brain`} ></i>
                  </div>
                }
                <div className="survey-item__info-title">
                  {P.dataTranslation(survey.customFormName, language)}
                </div>
              </div>
              <div className="survey-item__separator"></div>
              <div className="survey-item__center">
                <div className="survey-item__center-top">
                  <div className="survey-item__number-sendings">
                    {t("surveys.sentTo")} 
                    <b> {survey.customFormFrequency.length} </b> 
                    {
                      survey.customFormFrequency.length === 1 
                      ? t("globally.patient")
                      : t("globally.patients")
                    }
                  </div>
                  <div className="survey-item__center-separator"></div>
                  <div className="survey-item__number-answers">
                    {t("surveys.answeredTimes")} 
                    <b> {survey.userCustomFormHistories.length} </b> 
                    {
                      survey.userCustomFormHistories.length === 1 
                      ? t("surveys.answeredTime2")
                      : t("surveys.answeredTimes2")
                    }
                  </div>
                </div>
                {/* <div className="survey-item__delicate-results">
                  <div className={`survey-item__delicate-results-icon ${true && "survey-item__delicate-results-icon--alert"}`}>
                  </div>
                  <div className={`survey-item__delicate-results-text ${true && "survey-item__delicate-results-text--alert"}`}>
                    {t("surveys.delicateResults")} <b>0</b>
                  </div>
                </div> */}
              </div>
              <div className="survey-item__separator"></div>
              <div className="survey-item__buttons">
                <FlorenzButton
                  typeStyle={"secondaryWhite"}
                  size="s"
                  text={t("surveys.results")}
                  widthComplete={true}
                  onClick={() => survey.userCustomFormHistories.length > 0 && setSurveySelected(survey.id)}
                  disabled={survey.userCustomFormHistories.length === 0}
                  icon={survey.userCustomFormHistories.length === 0 && "zaia-i-lock-chat"}
                />
                <FlorenzButton
                  typeStyle={"outline"}
                  size="s"
                  text={t("surveys.preview")}
                  widthComplete={true}
                  onClick={() => setSurveyPreview(survey.id)}
                />
              </div>
            </div>
          )
        })
      }
      <div className="survey-list__no-more">
        {t("surveys.noMoreInfo")}
      </div>
    </div>
  );
};

export default SurveyList;